// ============================================================
// keypad.ts  — 画面上のテンキー（共通）
// ============================================================
//
// 配列は一般的なテンキーに統一する。
//   7 8 9
//   4 5 6
//   1 2 3
//     0
// 分類番号のように英字も使う入力では、数字の下に英字キーを並べる。
//
// 呼び出し側は決定された文字列 (キャンセル時は null) を受け取る。

export interface KeypadOptions {
    /** 見出し。どの項目を入力しているか分かる文言にする。 */
    title: string;
    /** 初期値。 */
    value?: string;
    /** 最大文字数。 */
    maxLength: number;
    /** 数字に加えて使える英字。省略時は数字のみ。 */
    letters?: string;
    /** 入力途中の値を整える。省略時は数字 (と letters) 以外を落とす。 */
    sanitize?: (value: string) => string;
    /** 単位などの補助表示。 */
    hint?: string;
}

interface KeypadParts {
    dialog: HTMLDialogElement;
    title: HTMLElement;
    hint: HTMLElement;
    draft: HTMLInputElement;
    digits: HTMLElement;
    letters: HTMLElement;
    back: HTMLButtonElement;
    clear: HTMLButtonElement;
    cancel: HTMLButtonElement;
    apply: HTMLButtonElement;
}

declare global {
    interface Window {
        __ncKeypad?: KeypadParts;
        __ncKeypadBound?: boolean;
    }
}

function build(): KeypadParts {
    const dialog = document.createElement('dialog');
    dialog.className = 'border border-dark rounded p-0 nc-keypad';
    dialog.setAttribute('aria-label', 'テンキー入力');

    const header = document.createElement('div');
    header.className = 'modal-header bg-dark text-white';
    const title = document.createElement('h5');
    title.className = 'modal-title';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'btn-close btn-close-white';
    close.setAttribute('aria-label', '閉じる');
    header.append(title, close);

    const body = document.createElement('div');
    body.className = 'modal-body';
    const hint = document.createElement('p');
    hint.className = 'small text-body-secondary';
    const draft = document.createElement('input');
    draft.type = 'text';
    draft.className = 'form-control form-control-lg fs-3 text-end mb-3';
    draft.autocomplete = 'off';
    draft.spellcheck = false;
    draft.inputMode = 'none';
    draft.setAttribute('aria-label', '入力値');
    const digits = document.createElement('div');
    digits.className = 'd-grid gap-2 nc-keypad-digits';
    const letters = document.createElement('div');
    letters.className = 'd-grid gap-2 mt-2 nc-keypad-letters';
    body.append(hint, draft, digits, letters);

    const footer = document.createElement('div');
    footer.className = 'modal-footer';
    const button = (label: string, className: string) => {
        const element = document.createElement('button');
        element.type = 'button';
        element.className = className;
        element.textContent = label;
        return element;
    };
    const back = button('1文字消す', 'btn btn-secondary');
    const clear = button('クリア', 'btn btn-secondary');
    const cancel = button('キャンセル', 'btn btn-secondary');
    const apply = button('決定', 'btn btn-primary');
    footer.append(back, clear, cancel, apply);

    dialog.append(header, body, footer);
    document.body.append(dialog);

    const built: KeypadParts = { dialog, title, hint, draft, digits, letters, back, clear, cancel, apply };
    close.addEventListener('click', () => dialog.close());
    return built;
}

/** テンキーを開く。決定なら入力値、キャンセル・閉じるなら null を返す。 */
export function openKeypad(options: KeypadOptions): Promise<string | null> {
    const parts = (window.__ncKeypad ??= build());
    const { dialog, title, hint, draft, digits, letters, back, clear, cancel, apply } = parts;

    const allowed = new RegExp(`[^0-9${options.letters ?? ''}]`, 'g');
    const sanitize = options.sanitize ?? ((value: string) =>
        value.normalize('NFKC').toUpperCase().replace(allowed, ''));
    const clean = () => { draft.value = sanitize(draft.value).slice(0, options.maxLength); };

    title.textContent = options.title;
    hint.textContent = options.hint ?? '';
    hint.hidden = !options.hint;
    draft.value = options.value ?? '';
    clean();

    const keys = (target: HTMLElement, characters: string[], columns: number) => {
        target.replaceChildren();
        target.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
        for (const character of characters) {
            if (!character) {
                const spacer = document.createElement('span');
                target.append(spacer);
                continue;
            }
            const key = document.createElement('button');
            key.type = 'button';
            key.className = 'btn btn-primary py-3 fs-4';
            key.textContent = character;
            key.addEventListener('click', () => { draft.value += character; clean(); });
            target.append(key);
        }
    };

    // 一般的なテンキー配列。最下段は 0 を中央に置く。
    keys(digits, ['7', '8', '9', '4', '5', '6', '1', '2', '3', '', '0', ''], 3);
    if (options.letters) {
        keys(letters, [...options.letters], 5);
        letters.hidden = false;
    } else {
        letters.replaceChildren();
        letters.hidden = true;
    }

    return new Promise<string | null>(resolve => {
        let settled = false;
        const finish = (value: string | null) => {
            if (settled) return;
            settled = true;
            back.removeEventListener('click', onBack);
            clear.removeEventListener('click', onClear);
            cancel.removeEventListener('click', onCancel);
            apply.removeEventListener('click', onApply);
            draft.removeEventListener('keydown', onKeyDown);
            dialog.removeEventListener('close', onClose);
            resolve(value);
        };
        const onBack = () => { draft.value = draft.value.slice(0, -1); };
        const onClear = () => { draft.value = ''; };
        const onCancel = () => dialog.close();
        const onApply = () => { clean(); const value = draft.value; dialog.close(); finish(value); };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') { event.preventDefault(); onApply(); }
        };
        const onClose = () => finish(null);

        back.addEventListener('click', onBack);
        clear.addEventListener('click', onClear);
        cancel.addEventListener('click', onCancel);
        apply.addEventListener('click', onApply);
        draft.addEventListener('keydown', onKeyDown);
        dialog.addEventListener('close', onClose);

        dialog.showModal();
        draft.focus({ preventScroll: true });
    });
}

// 入力欄に data-keypad-numeric を付けると、タップでテンキーが開く。
if (!window.__ncKeypadBound) {
    window.__ncKeypadBound = true;
    document.addEventListener('click', onKeypadInputClick);
}

async function onKeypadInputClick(event: MouseEvent) {
    const input = (event.target as Element | null)?.closest<HTMLInputElement>('input[data-keypad-numeric]');
    if (!input || input.disabled || input.readOnly) return;
    const result = await openKeypad({
        title: input.dataset.keypadTitle || input.getAttribute('aria-label') || '入力',
        value: input.value,
        maxLength: Number(input.dataset.keypadMaxlength) || (input.maxLength > 0 ? input.maxLength : 8),
        letters: input.dataset.keypadLetters || undefined,
        hint: input.dataset.keypadHint || undefined,
    });
    if (result === null || input.disabled || input.readOnly) return;
    input.value = result;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
}
